import { toPng, toJpeg, toSvg } from 'html-to-image';
import { getNodesBounds, getViewportForBounds, type Node } from '@xyflow/react';
import { downloadDataUrl } from './download';

const MAX_SIZE = 4096;
const MIN_SIZE = 720;

type CaptureOptions = {
  getNodes: () => Node[];
  backgroundColor?: string;
};

type ExportOptions = CaptureOptions & {
  filename: string;
};

function pickViewportElement(): HTMLElement | null {
  return document.querySelector('.cluster-flow .react-flow__viewport');
}

function computeExportSize(
  bounds: { width: number; height: number },
  padding = 0.16
) {
  const width = Math.ceil(bounds.width * (1 + padding * 2) + 80);
  const height = Math.ceil(bounds.height * (1 + padding * 2) + 80);
  return {
    width: Math.min(MAX_SIZE, Math.max(MIN_SIZE, width)),
    height: Math.min(MAX_SIZE, Math.max(MIN_SIZE, height))
  };
}

async function captureViewport({
  getNodes,
  backgroundColor = '#ffffff'
}: CaptureOptions) {
  const nodes = getNodes();
  if (!nodes.length) {
    throw new Error('هیچ نودی برای خروجی تصویر وجود ندارد.');
  }

  const viewport = pickViewportElement();
  if (!viewport) {
    throw new Error('محیط نمودار پیدا نشد.');
  }

  const bounds = getNodesBounds(nodes);
  const { width, height } = computeExportSize(bounds);
  const view = getViewportForBounds(bounds, width, height, 0.1, 2, 0.16);

  const style = {
    width: `${width}px`,
    height: `${height}px`,
    transform: `translate(${view.x}px, ${view.y}px) scale(${view.zoom})`
  };

  return { viewport, width, height, style, backgroundColor };
}

const defaultFilter = (node: HTMLElement): boolean => {
  const className = node?.classList;
  if (!className) return true;
  return (
    !className.contains('react-flow__minimap') &&
    !className.contains('react-flow__controls') &&
    !className.contains('cluster-legend') &&
    !className.contains('cluster-node__toggle')
  );
};

export async function captureFlowPng({
  getNodes,
  backgroundColor
}: CaptureOptions): Promise<string> {
  const { viewport, width, height, style } = await captureViewport({
    getNodes,
    backgroundColor
  });
  return toPng(viewport, {
    backgroundColor: backgroundColor ?? '#ffffff',
    width,
    height,
    style,
    pixelRatio: 2,
    filter: defaultFilter
  });
}

export async function exportFlowPng({
  getNodes,
  filename,
  backgroundColor
}: ExportOptions): Promise<string> {
  const dataUrl = await captureFlowPng({ getNodes, backgroundColor });
  downloadDataUrl(dataUrl, filename);
  return filename;
}

export async function exportFlowJpeg({
  getNodes,
  filename,
  backgroundColor
}: ExportOptions): Promise<string> {
  const { viewport, width, height, style } = await captureViewport({
    getNodes,
    backgroundColor
  });
  const dataUrl = await toJpeg(viewport, {
    backgroundColor: backgroundColor ?? '#ffffff',
    width,
    height,
    style,
    quality: 0.95,
    pixelRatio: 2,
    filter: defaultFilter
  });
  downloadDataUrl(dataUrl, filename);
  return filename;
}

export async function exportFlowSvg({
  getNodes,
  filename,
  backgroundColor
}: ExportOptions): Promise<string> {
  const { viewport, width, height, style } = await captureViewport({
    getNodes,
    backgroundColor
  });
  const dataUrl = await toSvg(viewport, {
    backgroundColor: backgroundColor ?? '#ffffff',
    width,
    height,
    style,
    filter: defaultFilter
  });
  downloadDataUrl(dataUrl, filename);
  return filename;
}
